import { create } from 'zustand';
import { customerService } from '../services/customerService';
import type { Customer } from '../services/customerService';

interface CustomerStore {
  customers: Customer[];
  loading: boolean;
  error: string | null;
  _isLoading: boolean; // Internal flag to prevent multiple calls
  loadCustomers: () => Promise<void>;
  addCustomer: (customer: Customer) => void;
  updateLocalCustomer: (id: string, updates: any) => Promise<void>;
  deleteLocalCustomer: (id: string) => Promise<void>;
  clearError: () => void;
}



export const useCustomerStore = create<CustomerStore>((set, get) => ({
  customers: [],
  loading: false,
  error: null,
  _isLoading: false, // Internal flag to prevent multiple calls

  loadCustomers: async () => {
    // Prevent multiple simultaneous calls
    const state = get();
    if (state.loading || state._isLoading) {
      return;
    }
    
    set({ loading: true, error: null, _isLoading: true });
    try {
      // Fetch from local JARVIS database
      const localCustomers = await customerService.getAllLocalCustomers();
      // Sort: newest first by created_at (fallback to updated_at)
      const sorted = [...localCustomers].sort((a, b) => {
        const da = new Date(a.created_at || a.updated_at || 0).getTime();
        const db = new Date(b.created_at || b.updated_at || 0).getTime();
        return db - da;
      });
      set({ customers: sorted, loading: false, _isLoading: false });
    } catch (error) {
      console.error('Error loading local customers:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load customers';
      set({ error: errorMessage, loading: false, _isLoading: false });
    }
  },

  addCustomer: (customer: Customer) => {
    set((state) => ({
      customers: [...state.customers, customer]
    }));
  },

  updateCustomer: (id: string, updates: Partial<Customer>) => {
    set((state) => ({
      customers: state.customers.map(customer =>
        customer.id === id ? { ...customer, ...updates } : customer
      )
    }));
  },

  deleteCustomer: (id: string) => {
    set((state) => ({
      customers: state.customers.filter(customer => customer.id !== id)
    }));
  },

  clearError: () => {
    set({ error: null });
  },

  updateLocalCustomer: async (id: string, updates: any) => {
    set({ loading: true, error: null });
    try {
      const updatedCustomer = await customerService.updateLocalCustomer(id, updates);
      set((state) => ({
        customers: state.customers.map(customer =>
          customer.id === id ? updatedCustomer : customer
        ),
        loading: false
      }));
    } catch (error) {
      console.error('Error updating local customer:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update customer';
      set({ error: errorMessage, loading: false });
      throw error instanceof Error ? error : new Error(errorMessage);
    }
  },

  deleteLocalCustomer: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await customerService.deleteLocalCustomer(id);
      set((state) => ({
        customers: state.customers.filter(customer => customer.id !== id),
        loading: false
      }));
    } catch (error) {
      console.error('Error deleting local customer:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete customer';
      set({ error: errorMessage, loading: false });
    }
  }
})); 